import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo, useState } from 'react';
import { ApiConnectionDto } from 'src/api/FiretrolApiClient';
import { useApiConnections } from 'src/api/queries/organization/useApiConnections';
import { formatDateTime } from 'src/utils';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { ApiConnectionDialog } from './components/ApiConnectionDialog';

const getColumns = (): MRT_ColumnDef<ApiConnectionDto>[] => [
  {
    accessorKey: 'name',
    header: 'Name',
  },
  {
    accessorKey: 'baseUrl',
    header: 'Base URL',
  },
  {
    accessorKey: 'redactedApiKey',
    header: 'API Key',
    size: 120,
    Cell: ({ cell }) => cell.getValue<string | undefined>() ?? 'none',
  },
  {
    accessorKey: 'isEnabled',
    header: 'Enabled',
    size: 100,
    Cell: ({ cell }) => (
      <Badge color={cell.getValue<boolean>() ? 'green' : 'gray'}>
        {cell.getValue<boolean>() ? 'Yes' : 'No'}
      </Badge>
    ),
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated',
    Cell: ({ cell }) => formatDateTime(cell.getValue<Date>()),
  },
];

export const ApiConnections: React.FC = () => {
  const { data: connections = [], isLoading, refetch } = useApiConnections();
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    connection: ApiConnectionDto | null;
  }>({ isOpen: false, connection: null });

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'api-connections',
    columns,
    defaultSorting: [{ id: 'name', desc: false }],
  });

  const { table } = useTable({
    ...tableState,
    onRefresh: refetch,
    tableOptions: {
      enableColumnFilters: false,
      enableRowActions: true,
      renderRowActions: ({ row }) => (
        <Group gap="xs">
          <EditActionIcon
            onClick={() =>
              setDialogState({ isOpen: true, connection: row.original })
            }
          />
        </Group>
      ),
      enableClickToCopy: false,
      columns,
      data: connections,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          <CreateButton
            onClick={() => setDialogState({ isOpen: true, connection: null })}
          >
            Create Connection
          </CreateButton>
        </Group>
      ),
    },
    getRowId: (row) => row.id,
    totalCount: connections.length,
    isLoading,
    isFetching: false,
    isError: false,
  });

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [{ text: 'Settings' }, { text: 'API Connections' }],
        }}
        title="API Connections"
        helpText="Manage the host and API key for each external system. SCIM integrations and Proact use a connection from this list."
      />
      <ApiConnectionDialog
        open={dialogState.isOpen}
        onClose={() => setDialogState({ isOpen: false, connection: null })}
        onSave={() => {
          setDialogState({ isOpen: false, connection: null });
          refetch();
        }}
        connection={dialogState.connection}
      />
      {table}
    </>
  );
};
